import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle, AlertCircle, ClipboardCheck } from 'lucide-react';
import { surveyService } from '../services/survey.service';
import { Button } from '../components/ui';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import type { SurveyAnswer, SurveyQuestion } from '../types/survey';

function SurveyResponsePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('t') ? Number(searchParams.get('t')) : undefined;
  const userId = searchParams.get('u') ? Number(searchParams.get('u')) : undefined;

  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({});
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});

  const { data: questionnaire, isLoading, error } = useQuery({
    queryKey: ['survey-public', id, ticketId],
    queryFn: () => surveyService.getPublic(Number(id), ticketId),
    enabled: !!id,
    retry: 1,
  });

  const submitMutation = useMutation({
    mutationFn: (answerList: SurveyAnswer[]) =>
      surveyService.submit(Number(id), {
        user_id: userId || 0,
        ticket_id: ticketId || null,
        answers: answerList,
      }),
    onSuccess: () => setSubmitted(true),
  });

  const handleBooleanChange = (questionId: number, value: boolean) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, answer_boolean: value },
    }));
    clearError(questionId);
  };

  const handleTextChange = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, answer_text: value },
    }));
    clearError(questionId);
  };

  const handleSingleChoice = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, option_id: optionId },
    }));
    clearError(questionId);
  };

  const handleMultipleChoice = (questionId: number, optionId: number, checked: boolean) => {
    setAnswers((prev) => {
      const existing = prev[questionId];
      if (checked) {
        return {
          ...prev,
          [questionId]: { question_id: questionId, option_id: optionId },
        };
      } else {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      }
    });
    clearError(questionId);
  };

  const clearError = (questionId: number) => {
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  const validate = (questions: SurveyQuestion[]) => {
    const errs: Record<number, string> = {};
    for (const q of questions) {
      if (q.is_required && !answers[q.id]) {
        errs[q.id] = 'Esta pergunta é obrigatória';
      }
    }
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionnaire) return;

    const errs = validate(questionnaire.questions);
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      return;
    }

    submitMutation.mutate(Object.values(answers));
  };

  const renderQuestion = (question: SurveyQuestion, idx: number) => {
    const error = validationErrors[question.id];

    return (
      <div
        key={question.id}
        className={`bg-white dark:bg-gray-800 border rounded-xl p-6 ${
          error
            ? 'border-red-300 dark:border-red-600'
            : 'border-gray-200 dark:border-gray-700'
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm font-semibold flex items-center justify-center">
            {idx + 1}
          </span>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {question.question_text}
              {question.is_required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </p>
          </div>
        </div>

        <div className="ml-10">
          {question.question_type === 'boolean' && (
            <div className="flex gap-4">
              {[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ].map(({ label, value }) => (
                <label
                  key={label}
                  className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border transition-colors ${
                    answers[question.id]?.answer_boolean === value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    name={`q-${question.id}`}
                    checked={answers[question.id]?.answer_boolean === value}
                    onChange={() => handleBooleanChange(question.id, value)}
                  />
                  <span className="font-medium">{label}</span>
                </label>
              ))}
            </div>
          )}

          {question.question_type === 'text' && (
            <textarea
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              placeholder="Digite sua resposta..."
              value={answers[question.id]?.answer_text || ''}
              onChange={(e) => handleTextChange(question.id, e.target.value)}
            />
          )}

          {(question.question_type === 'single_choice' || question.question_type === 'multiple_choice') && (
            <div className="space-y-2">
              {question.options.map((opt) => (
                <label
                  key={opt.id}
                  className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-lg border transition-colors ${
                    answers[question.id]?.option_id === opt.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <input
                    type={question.question_type === 'single_choice' ? 'radio' : 'checkbox'}
                    className="sr-only"
                    name={`q-${question.id}`}
                    checked={answers[question.id]?.option_id === opt.id}
                    onChange={(e) =>
                      question.question_type === 'single_choice'
                        ? handleSingleChoice(question.id, opt.id)
                        : handleMultipleChoice(question.id, opt.id, e.target.checked)
                    }
                  />
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-${question.question_type === 'single_choice' ? 'full' : 'md'} border-2 flex items-center justify-center ${
                      answers[question.id]?.option_id === opt.id
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300 dark:border-gray-500'
                    }`}
                  >
                    {answers[question.id]?.option_id === opt.id && (
                      <span className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </span>
                  <span>{opt.option_text}</span>
                </label>
              ))}
            </div>
          )}

          {error && (
            <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {error}
            </p>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Questionário não encontrado
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Este questionário não existe ou não está disponível.
          </p>
        </div>
      </div>
    );
  }

  if (submitted || submitMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Obrigado pelo seu feedback!
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Suas respostas foram registradas com sucesso.
          </p>
        </div>
      </div>
    );
  }

  if (questionnaire.already_answered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-blue-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Questionário já respondido
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Este questionário já foi respondido para este atendimento.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {questionnaire.title}
              </h1>
              <p className="text-sm text-gray-500">
                {questionnaire.questions.length} pergunta{questionnaire.questions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          {questionnaire.description && (
            <p className="text-gray-600 dark:text-gray-400">{questionnaire.description}</p>
          )}
        </div>

        {/* Questions */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {questionnaire.questions.map((q, idx) => renderQuestion(q, idx))}

          {submitMutation.isError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">
                Ocorreu um erro ao enviar suas respostas. Por favor, tente novamente.
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              isLoading={submitMutation.isPending}
              className="px-8"
            >
              Enviar Respostas
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-gray-400 mt-8">
          Os campos marcados com <span className="text-red-500">*</span> são obrigatórios.
        </p>

        {/* Footer */}
        <footer className="mt-10 pb-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs text-gray-400">
            <span>Este questionário faz parte do sistema</span>
            <a
              href="https://www.mahia.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gray-500 hover:text-blue-600 transition-colors"
            >
              MAHIA
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="https://www.mahia.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 transition-colors"
            >
              www.mahia.com.br
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default SurveyResponsePage;
